import React from 'react';
import type { VisitSlot } from '../../../dtos/portal/portal.dto';
import { formatClinician, formatPortalDate, formatPortalTime } from '../format';

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Calendar tile parts ('OCT', '12') for a YYYY-MM-DD date; empty when unparseable. */
function tileParts(value: string): { month: string; day: string } {
  const match = DATE_ONLY.exec(value.slice(0, 10));
  if (!match) return { month: '', day: '' };
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return { month: date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(), day: String(date.getDate()) };
}

/** One visit window: date tile, window, discipline, clinician first name + credential. */
const VisitSlotCard: React.FC<{ slot: VisitSlot }> = ({ slot }) => {
  const cancelled = slot.status === 'CANCELLED';
  const { month, day } = tileParts(slot.date);
  return (
    <div className="flex items-center gap-4">
      <div
        aria-hidden="true"
        className={`flex h-16 w-16 shrink-0 flex-col items-center justify-center rounded-xl ${
          cancelled ? 'bg-slate-100 text-slate-600' : 'bg-teal-50 text-teal-800'
        }`}
      >
        <span className="text-xs font-semibold tracking-wide">{month}</span>
        <span className="text-2xl font-bold leading-none">{day}</span>
      </div>
      <div className="flex min-w-0 flex-col gap-0.5">
        <p className={`font-semibold text-slate-900 ${cancelled ? 'line-through' : ''}`}>{formatPortalDate(slot.date)}</p>
        <p className="text-sm text-slate-700">
          {formatPortalTime(slot.windowStart)} – {formatPortalTime(slot.windowEnd)} · {slot.discipline}
        </p>
        <p className="text-sm text-slate-700">Clinician: {formatClinician(slot.clinician)}</p>
        {cancelled && <p className="text-sm font-medium text-red-800">Cancelled</p>}
      </div>
    </div>
  );
};

export default VisitSlotCard;
